import { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import RestoreIcon from '@mui/icons-material/Restore';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import type { ProjectSnapshot, SnapshotSummary } from '../../api/types';
import { opsApi } from '../../api/client';
import {
  useCreateSnapshot,
  useDeleteSnapshot,
  useImportSnapshot,
  useProjectSnapshots,
  useRestoreSnapshot,
} from '../../hooks/useOps';

interface BackupsDialogProps {
  projectId: string;
  open: boolean;
  onClose: () => void;
}

/** One-line "12 tracks · 68 items · 6 milestones · 9 logs · 8 photos" summary. */
function summaryLine(s: SnapshotSummary | null): string {
  if (!s) return '';
  const parts: string[] = [
    `${s.tracks} tracks`,
    `${s.items} items`,
    s.milestones ? `${s.milestones} milestones` : '',
    s.dependencies ? `${s.dependencies} deps` : '',
    s.holidays ? `${s.holidays} holidays` : '',
    s.dailyLogs ? `${s.dailyLogs} logs` : '',
    s.photos ? `${s.photos} photos` : '',
  ].filter(Boolean);
  return parts.join(' · ');
}

/** Filesystem-safe slug from a backup name for the downloaded filename. */
function fileSlug(name: string): string {
  return (
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 40) || 'backup'
  );
}

function whenLabel(iso: string): string {
  const d = new Date(iso);
  return new Intl.DateTimeFormat('id-ID', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
}

/**
 * Per-project backups: save a full snapshot of the current state, then restore
 * one in place (overwrites everything — confirmed first). Internal/owner action
 * — reached from the Plan tab.
 */
export function BackupsDialog({ projectId, open, onClose }: BackupsDialogProps) {
  const { data: snapshots = [], isLoading } = useProjectSnapshots(open ? projectId : undefined);
  const create = useCreateSnapshot(projectId);
  const restore = useRestoreSnapshot(projectId);
  const remove = useDeleteSnapshot(projectId);
  const importBackup = useImportSnapshot(projectId);

  const [name, setName] = useState('');
  const [confirmRestore, setConfirmRestore] = useState<ProjectSnapshot | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const busy =
    create.isPending || restore.isPending || remove.isPending || importBackup.isPending;

  const save = async () => {
    setErr(null);
    try {
      await create.mutateAsync(name.trim() || undefined);
      setName('');
      setMsg('Backup saved.');
    } catch (e) {
      setErr((e as Error).message ?? 'Failed to save backup');
    }
  };

  const doRestore = async (snap: ProjectSnapshot) => {
    setErr(null);
    try {
      await restore.mutateAsync(snap.id);
      setConfirmRestore(null);
      setMsg(`Project restored from "${snap.name}".`);
    } catch (e) {
      setErr((e as Error).message ?? 'Failed to restore backup');
    }
  };

  const doDelete = async (snap: ProjectSnapshot) => {
    setErr(null);
    try {
      await remove.mutateAsync(snap.id);
    } catch (e) {
      setErr((e as Error).message ?? 'Failed to delete backup');
    }
  };

  // Download a backup as a portable .zip (data + every photo/document file).
  const doExport = async (snap: ProjectSnapshot) => {
    setErr(null);
    try {
      const blob = await opsApi.exportSnapshot(projectId, snap.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${fileSlug(snap.name)}.dikonek-backup.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErr((e as Error).message ?? 'Failed to export backup');
    }
  };

  // Upload a .zip backup bundle and turn it into a backup in the list.
  const onPickFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = ''; // allow re-picking the same file
    if (!f) return;
    setErr(null);
    try {
      const form = new FormData();
      form.append('file', f);
      await importBackup.mutateAsync(form);
      setMsg('Backup imported — restore it from the list below.');
    } catch (e) {
      setErr((e as Error).message ?? 'Failed to import backup');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Project backups</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Save a full snapshot of this project (tracks, schedule, milestones, holidays, daily logs
          &amp; photos). Restoring a backup overwrites the project's current state — handy for
          experimenting and then rolling back. You can also download a backup as a <strong>.zip</strong>{' '}
          (the data plus every photo &amp; document file) and import it again later, or on another
          machine.
        </Typography>

        {/* Save current state */}
        <Stack direction="row" gap={1} alignItems="flex-start" sx={{ mb: 2 }}>
          <TextField
            size="small"
            fullWidth
            label="Backup name (optional)"
            placeholder="e.g. Before moving kickoff"
            value={name}
            onChange={(e) => setName(e.target.value)}
            inputProps={{ maxLength: 120 }}
            disabled={busy}
          />
          <Button
            variant="contained"
            startIcon={<SaveOutlinedIcon />}
            onClick={save}
            disabled={busy}
            sx={{ whiteSpace: 'nowrap', flex: '0 0 auto' }}
          >
            Save backup
          </Button>
        </Stack>

        {/* Import a backup file saved earlier (or moved from another machine). */}
        <input ref={fileRef} type="file" accept="application/zip,.zip" hidden onChange={onPickFile} />
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
          <Button
            size="small"
            startIcon={<FileUploadOutlinedIcon />}
            onClick={() => fileRef.current?.click()}
            disabled={busy}
          >
            Import from file…
          </Button>
        </Stack>

        {msg && (
          <Alert severity="success" onClose={() => setMsg(null)} sx={{ mb: 2 }}>
            {msg}
          </Alert>
        )}
        {err && (
          <Alert severity="error" onClose={() => setErr(null)} sx={{ mb: 2 }}>
            {err}
          </Alert>
        )}

        <Divider sx={{ mb: 1 }} />

        {isLoading ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            Loading backups…
          </Typography>
        ) : snapshots.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            No backups yet. Save one above to be able to roll back later.
          </Typography>
        ) : (
          <List dense disablePadding>
            {snapshots.map((snap) => {
              const confirming = confirmRestore?.id === snap.id;
              return (
                <ListItem
                  key={snap.id}
                  divider
                  disableGutters
                  sx={{ flexWrap: 'wrap', alignItems: 'flex-start', py: 1 }}
                  secondaryAction={
                    !confirming && (
                      <Stack direction="row" gap={0.5}>
                        <Tooltip title="Download as a .zip (data + all photos & files)">
                          <span>
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={() => doExport(snap)}
                              disabled={busy}
                            >
                              <FileDownloadOutlinedIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Restore this backup (overwrites current state)">
                          <span>
                            <IconButton
                              edge="end"
                              size="small"
                              color="primary"
                              onClick={() => setConfirmRestore(snap)}
                              disabled={busy}
                            >
                              <RestoreIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Delete this backup">
                          <span>
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={() => doDelete(snap)}
                              disabled={busy}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    )
                  }
                >
                  <Box sx={{ pr: 6, width: '100%' }}>
                    <Typography variant="subtitle2">{snap.name}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {whenLabel(snap.createdAt)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {summaryLine(snap.summary)}
                    </Typography>
                    {confirming && (
                      <Alert
                        severity="warning"
                        sx={{ mt: 1, width: '100%' }}
                        action={
                          <Stack direction="row" gap={1}>
                            <Button
                              size="small"
                              color="warning"
                              variant="contained"
                              onClick={() => doRestore(snap)}
                              disabled={busy}
                            >
                              Overwrite &amp; restore
                            </Button>
                            <Button size="small" onClick={() => setConfirmRestore(null)} disabled={busy}>
                              Cancel
                            </Button>
                          </Stack>
                        }
                      >
                        This replaces the project's current tracks, schedule, milestones &amp; logs
                        with this backup. The current state is discarded.
                      </Alert>
                    )}
                  </Box>
                </ListItem>
              );
            })}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
